// 文字起こしセグメントの純粋ロジック（タイムスタンプのアンカーと重複排除）。
// Web Speech API のリスタートを跨いだ挙動の要なので、変更時は必ずテストを更新すること。

/**
 * 録音開始からの経過時間を "[MM:SS] " / "[HH:MM:SS] " 形式で返す。
 * どちらかの時刻が無い場合は空文字。1時間未満は時間部分を省略する。
 */
export const formatElapsedTimestamp = (
	recordingStartTime: number | null,
	segmentStartTime: number | null,
): string => {
	if (!recordingStartTime || !segmentStartTime) {
		return "";
	}

	const elapsed = segmentStartTime - recordingStartTime;
	const totalSeconds = Math.floor(elapsed / 1000);

	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	const hoursString = hours.toString().padStart(2, "0");
	const minutesString = minutes.toString().padStart(2, "0");
	const secondsString = seconds.toString().padStart(2, "0");

	if (hours > 0) {
		return `[${hoursString}:${minutesString}:${secondsString}] `;
	}
	return `[${minutesString}:${secondsString}] `;
};

/** 重複排除キャッシュの上限（超えたらクリア） */
export const SEGMENT_CACHE_CAP = 100;

/**
 * 認識セグメントのタイムスタンプ付与と重複排除を担うトラッカー。
 *
 * QUIRK（保持必須の仕様）:
 * - タイムスタンプは「最初の interim 結果が届いた時刻」でアンカーする（isFinal 時ではない）。
 *   これにより [MM:SS] は発話開始時点を指す。
 * - recordingStartTime はリスタートを跨いで維持し、録音停止時にのみリセットする
 *   （コミット ded9d2f の修正。毎リスタートで 00:00 に巻き戻るのを防ぐ）。
 * - 重複排除は先頭15文字のハッシュで行う。リスタート跨ぎで同じ final が
 *   二重に届くケースを抑止する（既知セグメントはタイムスタンプなしでそのまま返す）。
 */
export class TranscriptSegmenter {
	private recordedSegments = new Set<string>();
	private segmentStartTime: number | null = null;
	private recordingStartTime: number | null = null;

	/** 録音開始時刻を記録（初回のみ。リスタートでは上書きされない） */
	markRecordingStarted(now: number): void {
		if (this.recordingStartTime === null) {
			this.recordingStartTime = now;
		}
	}

	/** 録音停止時に呼ぶ。次の録音のために開始時刻をリセットする */
	resetRecordingStart(): void {
		this.recordingStartTime = null;
		this.segmentStartTime = null;
	}

	/** interim（中間結果）が届いたときに呼ぶ。セグメント開始時刻をアンカーする */
	noteInterim(now: number): void {
		if (this.segmentStartTime === null) {
			this.segmentStartTime = now;
		}
	}

	/**
	 * final（確定結果）を処理し、表示用の1行を返す。
	 * 新規セグメントならタイムスタンプ付き、既知（重複）ならそのまま返す。
	 */
	processFinal(transcript: string, now: number): string {
		const transcriptHash = transcript.substring(0, 15);

		if (!this.recordedSegments.has(transcriptHash)) {
			this.recordedSegments.add(transcriptHash);

			// interim が来ないまま final が届いた場合はここでアンカー
			if (this.segmentStartTime === null) {
				this.segmentStartTime = now;
			}

			const line = formatElapsedTimestamp(this.recordingStartTime, this.segmentStartTime) + transcript;
			this.segmentStartTime = null;
			return line;
		}

		// 既に処理済みのセグメントはタイムスタンプなしでそのまま返す
		return transcript;
	}

	/** キャッシュが上限を超えていたらクリアする（定期メンテナンス用） */
	clearSegmentsIfOverCap(cap: number = SEGMENT_CACHE_CAP): void {
		if (this.recordedSegments.size > cap) {
			this.recordedSegments.clear();
		}
	}

	/** 重複排除キャッシュをクリアする（認識セッションの開始時） */
	clearSegments(): void {
		this.recordedSegments.clear();
	}

	get segmentCount(): number {
		return this.recordedSegments.size;
	}

	get hasRecordingStarted(): boolean {
		return this.recordingStartTime !== null;
	}
}
